/**
 * CSV Export utilities for Budget Widget
 */

import type { BudgetTableEntry, ExportResponse } from '../types';
import { categoryLabel, t } from '../i18n';

/** Delimiter hint Excel reads before the header row; see downloadCSV. */
const SEPARATOR_HINT = 'sep=;\r\n';

interface CSVColumn {
  header: string;
  getValue: (entry: BudgetTableEntry) => string | number;
}

/**
 * Escapes a CSV value for proper handling of separators, quotes, newlines, and
 * formula-injection characters. Cells starting with =/+/-/@/\t/\r are otherwise
 * interpreted as formulas by Excel/LibreOffice; prefixing with an apostrophe
 * neutralizes that without changing what the user sees (the leading apostrophe
 * is hidden in Excel's display).
 *
 * Commas are quoted even though the delimiter is ';': an English-locale Excel
 * ignores our delimiter and splits on ',' unless the `sep=;` hint (see
 * downloadCSV) is honored, which tore rows with a comma in Zweckbestimmung
 * apart. Quoting makes the comma inert either way.
 */
export function escapeCSVValue(value: string | number): string {
  let stringValue = String(value);

  // Formula guard only applies to string inputs. A number like -1.5 starts
  // with '-' but isn't a formula injection vector — its type rules that out.
  if (typeof value === 'string' && stringValue.length > 0 && /^[=+\-@\t\r]/.test(stringValue)) {
    stringValue = "'" + stringValue;
  }

  // Check if value needs quoting (contains a separator, quote, or newline)
  if (/[;,"\n\r]/.test(stringValue)) {
    // Escape quotes by doubling them
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  return stringValue;
}

/**
 * Generates CSV content from budget entries
 * Uses semicolon delimiter for German Excel compatibility
 * Headers and category names follow the widget language; title-level texts
 * (Zweckbestimmung) stay in their original German wording.
 */
export function generateCSV(entries: BudgetTableEntry[], valueColumnLabel: string): string {
  const m = t().csv;
  const columns: CSVColumn[] = [
    { header: m.einzelplan, getValue: (e) => e.einzelplan },
    { header: m.kapitel, getValue: (e) => e.kapitel },
    { header: m.titelFunktion, getValue: (e) => e.prefix ? `${e.prefix} ${e.titel_funktion}` : e.titel_funktion },
    { header: m.zweckbestimmung, getValue: (e) => e.zweckbestimmung },
    { header: m.oberkategorie, getValue: (e) => categoryLabel(e.expenditure_type) },
    { header: m.unterkategorie, getValue: (e) => e.sub_expenditure_type ? categoryLabel(e.sub_expenditure_type) : '' },
    { header: valueColumnLabel, getValue: (e) => e.latest_soll },
  ];

  // Build header row
  const headerRow = columns.map(col => escapeCSVValue(col.header)).join(';');

  // Build data rows
  const dataRows = entries.map(entry =>
    columns.map(col => escapeCSVValue(col.getValue(entry))).join(';')
  );

  // Combine with newlines
  return [headerRow, ...dataRows].join('\r\n');
}

/**
 * Generates CSV content from export data with full time series columns
 */
export function generateTimeSeriesCSV(data: ExportResponse): string {
  const m = t().csv;
  // Static columns
  const staticHeaders = [
    m.einzelplan,
    m.kapitel,
    m.type,
    m.titelFunktion,
    m.zweckbestimmung,
    m.kategorie,
    m.unterkategorie,
  ];

  // Year columns: e.g. "Ist_2016", "Soll_2025" ("Actual_2016", "Planned_2025")
  const yearHeaders = data.years.map(({ year, valueType }) =>
    `${valueType === 'ist' ? m.ist : m.soll}_${year}`
  );

  const allHeaders = [...staticHeaders, ...yearHeaders, m.positionChange];
  const headerRow = allHeaders.map(h => escapeCSVValue(h)).join(';');

  const dataRows = data.entries.map(entry => {
    const staticValues = [
      entry.einzelplan,
      entry.kapitel,
      entry.type,
      entry.prefix ? `${entry.prefix} ${entry.titel_funktion}` : entry.titel_funktion,
      entry.zweckbestimmung,
      categoryLabel(entry.expenditure_type),
      entry.sub_expenditure_type ? categoryLabel(entry.sub_expenditure_type) : entry.sub_expenditure_type,
    ];

    const yearValues = data.years.map(({ year, valueType }) => {
      const key = `${valueType}_${year}`;
      const val = entry.values[key];
      return val != null ? val : '';
    });

    return [...staticValues, ...yearValues, entry.position_change].map(v => escapeCSVValue(v)).join(';');
  });

  return [headerRow, ...dataRows].join('\r\n');
}

/**
 * Triggers a browser download of the CSV content.
 *
 * Two Excel compatibility wrappers around the raw content:
 *   - a UTF-8 BOM, so German umlauts survive the import, and
 *   - a leading `sep=;` line, which every locale's Excel reads as "this file
 *     is semicolon-delimited". Without it an English-locale Excel splits on
 *     ',' instead and scatters every cell containing a comma across extra
 *     columns. Other readers (LibreOffice, pandas, R) either honor the hint
 *     or show it as a single first row.
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Add UTF-8 BOM for Excel compatibility with German umlauts
  const BOM = '\uFEFF';
  const contentWithBOM = BOM + SEPARATOR_HINT + csvContent;

  // Create blob with UTF-8 encoding
  const blob = new Blob([contentWithBOM], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename with current date
 */
export function generateExportFilename(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${t().csv.exportFilenamePrefix}_${year}-${month}-${day}.csv`;
}
